import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { CallsPage } from '@/features/calls/CallsPage';
import { CallDetailPage } from '@/features/calls/CallDetailPage';
import { OrdersPage } from '@/features/orders/OrdersPage';
import { PublicOrderPage } from '@/features/orders/PublicOrderPage';
import { MenuPage } from '@/features/menu/MenuPage';
import { AnswersPage } from '@/features/faqs/AnswersPage';
import { HoursPage } from '@/features/hours/HoursPage';
import { VoicePage } from '@/features/voice/VoicePage';
import { PronunciationTestPage } from '@/features/voice/PronunciationTestPage';
import { PhonePage } from '@/features/phone/PhonePage';
import { OnboardingWizard } from '@/features/onboarding/OnboardingWizard';
import { PosPage } from '@/features/integrations/PosPage';
import { PaymentSettingsPage } from '@/features/payments/PaymentSettingsPage';
import { BusinessPage } from '@/features/business/BusinessPage';
import { TeamPage } from '@/features/team/TeamPage';
const BillingPage = lazy(() =>
  import('@/features/billing/BillingPage').then((module) => ({ default: module.BillingPage }))
);
// livekit-client is only needed on the test page; keep it out of first paint.
const TestCallPage = lazy(() =>
  import('@/features/test/TestCallPage').then((module) => ({ default: module.TestCallPage }))
);
const AdminPage = lazy(() =>
  import('@/features/admin/AdminPage').then((module) => ({ default: module.AdminPage }))
);
const TelnyxCostsPage = lazy(() =>
  import('@/features/admin/TelnyxCostsPage').then((module) => ({ default: module.TelnyxCostsPage }))
);
import { useGetSessionQuery } from '@/store/api';
import { EmptyState } from '@/components/ui';

/** Waits for the session check before deciding, so a refresh does not bounce. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { data, isLoading, isError } = useGetSessionQuery();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-small text-slate">
        Loading your account…
      </div>
    );
  }
  if (isError || !data) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

const RouteFallback = () => (
  <p className="px-4 py-12 text-center text-small text-slate">Loading…</p>
);

/**
 * The API enforces this too. Checking here as well keeps a non-admin from
 * loading a screen whose every request would fail.
 */
function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  const { data } = useGetSessionQuery();
  if (!data?.user.isPlatformAdmin) {
    return (
      <EmptyState
        headline="Not available on this account"
        hint="The platform area is limited to CallWaiter staff accounts."
      />
    );
  }
  return <>{children}</>;
}

/**
 * Every screen inside the dashboard reads organization-scoped data. A platform
 * admin has no organization until they impersonate one, so sending them here
 * would render a page whose every request returns 403. They belong in /admin.
 */
function RequireOrganization({ children }: { children: React.ReactNode }) {
  const { data } = useGetSessionQuery();
  if (data && !data.organization) {
    return data.user.isPlatformAdmin ? (
      <Navigate to="/admin" replace />
    ) : (
      <EmptyState
        headline="No restaurant on this account"
        hint="This login is not a member of any restaurant yet. Ask an owner to invite you."
      />
    );
  }
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      {/* A customer's copy of their order, from the link in their text. No login. */}
      <Route path="/o/:token" element={<PublicOrderPage />} />

      {/* Setup runs outside the dashboard chrome: there is nothing to navigate
          to until it is finished. */}
      <Route
        path="/setup"
        element={
          <RequireAuth>
            <RequireOrganization>
              <OnboardingWizard />
            </RequireOrganization>
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route
          index
          element={
            <RequireOrganization>
              <DashboardPage />
            </RequireOrganization>
          }
        />
        <Route
          path="calls"
          element={
            <RequireOrganization>
              <CallsPage />
            </RequireOrganization>
          }
        />
        <Route
          path="calls/:id"
          element={
            <RequireOrganization>
              <CallDetailPage />
            </RequireOrganization>
          }
        />
        <Route
          path="orders"
          element={
            <RequireOrganization>
              <OrdersPage />
            </RequireOrganization>
          }
        />
        <Route
          path="menu"
          element={
            <RequireOrganization>
              <MenuPage />
            </RequireOrganization>
          }
        />
        <Route
          path="answers"
          element={
            <RequireOrganization>
              <AnswersPage />
            </RequireOrganization>
          }
        />
        <Route
          path="voice"
          element={
            <RequireOrganization>
              <VoicePage />
            </RequireOrganization>
          }
        />
        <Route
          path="test"
          element={
            <RequireOrganization>
              <Suspense fallback={<RouteFallback />}>
                <TestCallPage />
              </Suspense>
            </RequireOrganization>
          }
        />
        <Route
          path="pronunciation"
          element={
            <RequireOrganization>
              <PronunciationTestPage />
            </RequireOrganization>
          }
        />
        <Route
          path="hours"
          element={
            <RequireOrganization>
              <HoursPage />
            </RequireOrganization>
          }
        />
        <Route
          path="phone"
          element={
            <RequireOrganization>
              <PhonePage />
            </RequireOrganization>
          }
        />
        <Route
          path="integrations"
          element={
            <RequireOrganization>
              <PosPage />
            </RequireOrganization>
          }
        />
        <Route
          path="payments"
          element={
            <RequireOrganization>
              <PaymentSettingsPage />
            </RequireOrganization>
          }
        />
        <Route
          path="business"
          element={
            <RequireOrganization>
              <BusinessPage />
            </RequireOrganization>
          }
        />
        <Route
          path="team"
          element={
            <RequireOrganization>
              <TeamPage />
            </RequireOrganization>
          }
        />
        <Route
          path="billing"
          element={
            <RequireOrganization>
              <Suspense fallback={<RouteFallback />}>
                <BillingPage />
              </Suspense>
            </RequireOrganization>
          }
        />
        <Route
          path="admin"
          element={
            <RequirePlatformAdmin>
              <Suspense fallback={<RouteFallback />}>
                <AdminPage />
              </Suspense>
            </RequirePlatformAdmin>
          }
        />
        <Route
          path="admin/telnyx"
          element={
            <RequirePlatformAdmin>
              <Suspense fallback={<RouteFallback />}>
                <TelnyxCostsPage />
              </Suspense>
            </RequirePlatformAdmin>
          }
        />
      </Route>

      <Route
        path="*"
        element={
          <div className="flex min-h-screen items-center justify-center">
            <EmptyState headline="Page not found" hint="Check the address, or head back to your overview." />
          </div>
        }
      />
    </Routes>
  );
}
